import Phaser from 'phaser';
import type { CareerLevel } from './levels';

export interface WorkSpec {
  type: string;
  level: CareerLevel;
  label: string;
  shortLabel: string;
  value: number;
  deadlineMs: number;
  spawnWeight: number;
  iconKey: string;
  primary: number;
  secondary: number;
  textBadge: string;
}

export const STAGE_TEXTURES = {
  tbe: 'stage-tbe',
  approved: 'stage-approved',
  rejected: 'stage-rejected',
  fix: 'stage-fix'
} as const;

// All work specs across the four career levels. Spawn pool at any moment is
// filtered by current level. Values escalate per level so that a similar
// "five-items-in-60s" loop scales cash output ~3x per promotion.
type WorkSpecInput = Omit<WorkSpec, 'iconKey'>;

const SPECS: WorkSpecInput[] = [
  // Office Admin
  { type: 'fileDocs', level: 'admin', label: 'File Documents', shortLabel: 'FILE', value: 25, deadlineMs: 30000, spawnWeight: 5, primary: 0xfb923c, secondary: 0x9a3412, textBadge: 'FILE' },
  { type: 'phoneCall', level: 'admin', label: 'Answer Phone', shortLabel: 'PHONE', value: 40, deadlineMs: 28000, spawnWeight: 4, primary: 0xfde047, secondary: 0x713f12, textBadge: 'CALL' },
  { type: 'schedule', level: 'admin', label: 'Schedule Appt', shortLabel: 'SCHED', value: 60, deadlineMs: 25000, spawnWeight: 3, primary: 0x67e8f9, secondary: 0x155e75, textBadge: 'SCH' },
  { type: 'notarize', level: 'admin', label: 'Notarize', shortLabel: 'NOTAR', value: 90, deadlineMs: 20000, spawnWeight: 2, primary: 0xf87171, secondary: 0x7f1d1d, textBadge: 'NOT' },
  { type: 'mailRun', level: 'admin', label: 'Mail Run', shortLabel: 'MAIL', value: 120, deadlineMs: 15000, spawnWeight: 2, primary: 0x60a5fa, secondary: 0x1e3a8a, textBadge: 'MAIL' },

  // Paralegal
  { type: 'research', level: 'paralegal', label: 'Legal Research', shortLabel: 'RSCH', value: 80, deadlineMs: 28000, spawnWeight: 5, primary: 0x818cf8, secondary: 0x312e81, textBadge: 'RSCH' },
  { type: 'draftMotion', level: 'paralegal', label: 'Draft Motion', shortLabel: 'MOTN', value: 140, deadlineMs: 24000, spawnWeight: 4, primary: 0x6ee7b7, secondary: 0x064e3b, textBadge: 'MOTN' },
  { type: 'discovery', level: 'paralegal', label: 'Discovery', shortLabel: 'DISC', value: 200, deadlineMs: 20000, spawnWeight: 3, primary: 0xfb7185, secondary: 0x881337, textBadge: 'DISC' },
  { type: 'eFile', level: 'paralegal', label: 'E-File', shortLabel: 'EFLE', value: 260, deadlineMs: 16000, spawnWeight: 2, primary: 0x38bdf8, secondary: 0x0c4a6e, textBadge: 'EFIL' },
  { type: 'citeCheck', level: 'paralegal', label: 'Cite Check', shortLabel: 'CITE', value: 350, deadlineMs: 12000, spawnWeight: 1, primary: 0xa3e635, secondary: 0x365314, textBadge: 'CITE' },

  // Attorney
  { type: 'memo', level: 'attorney', label: 'Legal Memo', shortLabel: 'MEMO', value: 200, deadlineMs: 26000, spawnWeight: 5, primary: 0xc4b5fd, secondary: 0x4c1d95, textBadge: 'MEMO' },
  { type: 'deposition', level: 'attorney', label: 'Deposition', shortLabel: 'DEPO', value: 400, deadlineMs: 22000, spawnWeight: 3, primary: 0xf472b6, secondary: 0x831843, textBadge: 'DEPO' },
  { type: 'motionHearing', level: 'attorney', label: 'Motion Hearing', shortLabel: 'HEAR', value: 600, deadlineMs: 18000, spawnWeight: 2, primary: 0xfbbf24, secondary: 0x713f12, textBadge: 'HEAR' },
  { type: 'negotiate', level: 'attorney', label: 'Negotiate Deal', shortLabel: 'DEAL', value: 800, deadlineMs: 14000, spawnWeight: 2, primary: 0x5eead4, secondary: 0x134e4a, textBadge: 'NEG' },

  // Partner
  { type: 'clientPitch', level: 'partner', label: 'Client Pitch', shortLabel: 'PTCH', value: 400, deadlineMs: 28000, spawnWeight: 4, primary: 0xef4444, secondary: 0x7f1d1d, textBadge: 'PTCH' },
  { type: 'trialArg', level: 'partner', label: 'Trial Argument', shortLabel: 'TRIA', value: 900, deadlineMs: 22000, spawnWeight: 3, primary: 0x6366f1, secondary: 0x312e81, textBadge: 'TRIA' },
  { type: 'bigDeal', level: 'partner', label: 'Major Deal', shortLabel: 'DEAL', value: 1500, deadlineMs: 16000, spawnWeight: 2, primary: 0xfacc15, secondary: 0x713f12, textBadge: 'DEAL' },
  { type: 'originate', level: 'partner', label: 'Originate Client', shortLabel: 'ORIG', value: 2200, deadlineMs: 12000, spawnWeight: 1, primary: 0x34d399, secondary: 0x064e3b, textBadge: 'ORIG' }
];

export const allWorkSpecs: WorkSpec[] = SPECS.map((s) => ({ ...s, iconKey: `work-${s.type}` }));

export const workSpecsByType: Record<string, WorkSpec> = Object.fromEntries(
  allWorkSpecs.map((s) => [s.type, s])
);

const specsByLevelCache: Record<CareerLevel, WorkSpec[]> = {
  admin: allWorkSpecs.filter((s) => s.level === 'admin'),
  paralegal: allWorkSpecs.filter((s) => s.level === 'paralegal'),
  attorney: allWorkSpecs.filter((s) => s.level === 'attorney'),
  partner: allWorkSpecs.filter((s) => s.level === 'partner')
};

const ICON_SIZE = 32;

export function ensureWorkTextures(scene: Phaser.Scene): void {
  for (const spec of allWorkSpecs) {
    if (!scene.textures.exists(spec.iconKey)) drawIcon(scene, spec);
  }
  if (!scene.textures.exists(STAGE_TEXTURES.tbe))
    drawClipboard(scene, STAGE_TEXTURES.tbe, 0xd1d5db, 0x4b5563, 'none');
  if (!scene.textures.exists(STAGE_TEXTURES.approved))
    drawClipboard(scene, STAGE_TEXTURES.approved, 0xd1d5db, 0x4b5563, 'check');
  if (!scene.textures.exists(STAGE_TEXTURES.rejected))
    drawClipboard(scene, STAGE_TEXTURES.rejected, 0xd1d5db, 0x4b5563, 'x');
  if (!scene.textures.exists(STAGE_TEXTURES.fix))
    drawWrench(scene, STAGE_TEXTURES.fix);
}

function drawIcon(scene: Phaser.Scene, spec: WorkSpec): void {
  const tex = scene.textures.createCanvas(spec.iconKey, ICON_SIZE, ICON_SIZE);
  if (!tex) return;
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, ICON_SIZE, ICON_SIZE);

  roundRectPath(ctx, 1, 1, ICON_SIZE - 2, ICON_SIZE - 2, 5);
  ctx.fillStyle = hex(spec.primary);
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = hex(spec.secondary);
  ctx.stroke();

  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  roundRectPath(ctx, 3, 3, ICON_SIZE - 6, 8, 3);
  ctx.fill();

  // Font size shrinks for longer badges so 5-letter labels still fit the 32px card.
  const badge = spec.textBadge;
  const fontSize = badge.length <= 3 ? 13 : badge.length <= 4 ? 11 : 9;
  ctx.fillStyle = hex(spec.secondary);
  ctx.font = `700 ${fontSize}px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(badge, ICON_SIZE / 2, ICON_SIZE / 2 + 1);

  tex.refresh();
}

function drawClipboard(
  scene: Phaser.Scene,
  key: string,
  fill: number,
  edge: number,
  badge: 'check' | 'x' | 'none'
): void {
  const tex = scene.textures.createCanvas(key, ICON_SIZE, ICON_SIZE);
  if (!tex) return;
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, ICON_SIZE, ICON_SIZE);

  roundRectPath(ctx, 1, 1, ICON_SIZE - 2, ICON_SIZE - 2, 5);
  ctx.fillStyle = hex(fill);
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = hex(edge);
  ctx.stroke();

  ctx.fillStyle = hex(edge);
  roundRectPath(ctx, 12, 1, 8, 4, 1);
  ctx.fill();

  ctx.strokeStyle = hex(edge);
  ctx.lineWidth = 1.5;
  drawLine(ctx, 8, 13, 24, 13);
  drawLine(ctx, 8, 18, 24, 18);
  drawLine(ctx, 8, 23, 19, 23);

  if (badge === 'check') {
    ctx.fillStyle = '#16a34a';
    ctx.beginPath();
    ctx.arc(25, 25, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(22, 25);
    ctx.lineTo(24.5, 27.5);
    ctx.lineTo(28, 23);
    ctx.stroke();
  } else if (badge === 'x') {
    ctx.fillStyle = '#dc2626';
    ctx.beginPath();
    ctx.arc(25, 25, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    drawLine(ctx, 22, 22, 28, 28);
    drawLine(ctx, 28, 22, 22, 28);
  }

  tex.refresh();
}

function drawWrench(scene: Phaser.Scene, key: string): void {
  const tex = scene.textures.createCanvas(key, ICON_SIZE, ICON_SIZE);
  if (!tex) return;
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, ICON_SIZE, ICON_SIZE);

  roundRectPath(ctx, 1, 1, ICON_SIZE - 2, ICON_SIZE - 2, 5);
  ctx.fillStyle = '#60a5fa';
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#1e3a8a';
  ctx.stroke();

  ctx.fillStyle = '#1e3a8a';
  ctx.beginPath();
  ctx.arc(16, 16, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#60a5fa';
  ctx.beginPath();
  ctx.arc(16, 16, 4, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = '#1e3a8a';
  ctx.lineWidth = 2;
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    const x1 = 16 + Math.cos(a) * 8.5;
    const y1 = 16 + Math.sin(a) * 8.5;
    const x2 = 16 + Math.cos(a) * 12.5;
    const y2 = 16 + Math.sin(a) * 12.5;
    drawLine(ctx, x1, y1, x2, y2);
  }

  tex.refresh();
}

// ----- helpers ------------------------------------------------------

function hex(n: number): string {
  return '#' + n.toString(16).padStart(6, '0');
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

// ----- spawn pool selection ----------------------------------------

export function specsForLevel(level: CareerLevel): WorkSpec[] {
  return specsByLevelCache[level];
}

export function pickRandomSpec(level: CareerLevel, rand: () => number = Math.random): WorkSpec | null {
  const pool = specsByLevelCache[level];
  if (pool.length === 0) return null;
  const totalWeight = pool.reduce((sum, s) => sum + s.spawnWeight, 0);
  let r = rand() * totalWeight;
  for (const s of pool) {
    r -= s.spawnWeight;
    if (r <= 0) return s;
  }
  return pool[pool.length - 1];
}
